import React, { useState, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import {
  faCircleNotch,
  faCopy,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons'

import CarbonOffset from './CarbonOffset'
import InvalidForm from 'src/components/InvalidForm'

import formatNumber from 'src/utils/formatNumber'

const Info = ({
  state, stepTwo, statusUrl, setDonation, errors,
}) => {
  const [ donation, setInternalDonation ] = useState()

  const expiry = useRef()

  if (!expiry.current)
    expiry.current = new Date(Date.now() + 60*60*1000)

  const calcTimeLeft = () => expiry.current - Date.now()

  const [ timeLeft, setTimeLeft ] = useState(calcTimeLeft)

  useEffect(() => {
    if (donation) return

    const interval = setInterval(() => {
      const timeLeft = calcTimeLeft()
      setTimeLeft(timeLeft)
      if (timeLeft <= 0)
        clearInterval(interval)
    }, 1000)

    return () => clearInterval(interval)
  }, [ donation ])

  useEffect(() => {
    let timeout
    // Latched so the hand-off can be scheduled at most once, and never after
    // teardown. Two ways a late poll would otherwise schedule work nothing can
    // cancel — both ending with the donor yanked off the tab they moved to:
    //   - Overlapping ticks. Fetches aren't aborted or in-flight-guarded, so a
    //     response slower than the 10s interval resolves alongside the next
    //     one; both see `order` and the second assignment orphans the first
    //     handle, leaving it unclearable.
    //   - A poll resolving after cleanup. Needs no overlap at all: navigate
    //     away mid-request and the cleanup below runs with nothing to clear,
    //     then the response lands and schedules a fresh timeout. (Note neither
    //     `if (timeout) return` nor clearing before reassignment catches this
    //     one — the latch is what closes it.)
    let handedOff = false

    const checkStatus = async () => {
      // Stop polling once the address has expired. The effect deps
      // are [] so re-rendering on timeLeft hitting 0 doesn't tear
      // this down; we read calcTimeLeft() at tick time instead.
      // Without this guard a donor who opens crypto and walks away
      // would keep hitting statusUrl every 10s for as long as the
      // panel stayed mounted, well past the 1-hour address lifetime.
      if (calcTimeLeft() <= 0) {
        clearInterval(interval)
        return
      }
      const url = `${statusUrl}?address=${encodeURIComponent(stepTwo.address)}`

      try {
        const response = await fetch(url, { headers: { Accept: 'application/json' } })
        if (response.ok) {
          const { order, token } = await response.json()
          if (order && !handedOff) {
            handedOff = true
            // Flatten the serialized order + token into a single shape
            // for both the local "received" display and the App-level
            // donation propagated via setDonation (used downstream for
            // ThankYouInfo + post-donation comment, both keyed on
            // donation.token).
            const settled = { ...order, token }
            setInternalDonation(settled)
            clearInterval(interval)
            // Tracked so the cleanup below can cancel it: the donor can switch
            // payment tabs inside this 3s window (the tabs stay live while
            // App-level `donation` is null), and an uncancelled hand-off would
            // then fire against a panel they'd already left — yanking them to
            // the thank-you screen and publishing a donationCompleted labelled
            // with whatever method was selected by then. v7 gets this from
            // structure instead: its timeout lives in an effect keyed on
            // [ received ], so React's own cleanup guarantees one live handle.
            timeout = setTimeout(() => setDonation(settled), 3000)
          }
        }
      } catch (error) {
        console.error(error)
      }
    }

    const interval = setInterval(checkStatus, 10000)
    return () => {
      // Latch first: a fetch already in flight will resolve after this runs, and
      // without this it would schedule a hand-off with no cleanup left to cancel
      // it. clearTimeout only helps for a hand-off already scheduled.
      handedOff = true
      clearInterval(interval)
      clearTimeout(timeout)
    }
  }, [])

  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60))
  const seconds = Math.floor((timeLeft % (1000 * 60)) / 1000)

  const lpad = number => {
    number = number.toString()
    return number.length > 1 ? number : '0' + number
  }

  return (
    <div>
      <InvalidForm>{errors?.base?.[0]}</InvalidForm>

      <h2 className="h5 section-heading section-heading-donated">
        {donation ? 'Thank You for Your Donation!' : 'Send Donation'}
      </h2>
      {
        donation ?
        <p>
          Your donation of <b>
            {formatNumber(donation.amount)} {donation.currency.iso_code}
          </b> has been received.
        </p>
        : timeLeft > 0 ?
        <p>
          Please scan in your cryptocurrency wallet or copy the address to send
          your <b>
            {formatNumber(state.crypto_amount.toString())} {state.crypto_currency}
          </b> donation:
        </p>
        :
        <p>This address has expired.</p>
      }
      <p className="text-center">
        <img
          src={`https://api.qrserver.com/v1/create-qr-code/?${
            new URLSearchParams({ data: stepTwo.address, size: '144x144' })
          }`}
          alt={stepTwo.address}
        />
      </p>
      <div>
        <h3 className="h6">
          Address
        </h3>
        <div className="position-relative mb-3">
          <pre
            id="address"
            className="bg-light rounded border p-2 pr-5"
            style={timeLeft > 0 ? null : { textDecoration: 'line-through' }}
          >{stepTwo.address}</pre>
          {
            timeLeft > 0 &&
            <button
              className="btn btn-sm btn-dark position-absolute top-0 end-0 m-1"
              data-clipboard-target="#address"
              aria-label="Copy"
            >
              <FontAwesomeIcon icon={faCopy} />
            </button>
          }
        </div>
      </div>
      {
        stepTwo.network_name &&
          <div>
            <h3 className="h6">
              Network
            </h3>
            <p className="bg-light rounded border p-2 mb-3 fw-bold">
              {stepTwo.network_name}
            </p>
          </div>
      }
      {
        stepTwo.destination_tag &&
          <div>
            <h3 className="h6">
              Destination Tag / Memo
            </h3>
            <div className="position-relative mb-3">
              <pre
                id="destination-tag"
                className="bg-light rounded border p-2 pr-5"
                style={timeLeft > 0 ? null : { textDecoration: 'line-through' }}
              >{stepTwo.destination_tag}</pre>
              {
                timeLeft > 0 &&
                  <button
                    className="btn btn-sm btn-dark position-absolute top-0 end-0 m-1"
                    data-clipboard-target="#destination-tag"
                    aria-label="Copy"
                  >
                    <FontAwesomeIcon icon={faCopy} />
                  </button>
              }
            </div>
          </div>
      }
      {
        stepTwo.network_warning && stepTwo.network_name && !donation &&
          <div className="bg-light rounded-lg p-3 mb-3">
            <p className="mb-0">
              <FontAwesomeIcon
                icon={faExclamationTriangle}
                className="me-2 text-warning"
              />
              Send only on the {stepTwo.network_name} network. Crypto sent on
              any other network may be lost.
            </p>
          </div>
      }
      <p>
        {
          donation ?
          <b>Status: <span className="text-success">Donation received</span></b>
          : timeLeft > 0 ?
          <b>
            Status: <span className="text-info">
              Awaiting donation <FontAwesomeIcon icon={faCircleNotch} spin />
            </span>
            <br />
            <small className="text-muted">
              It may take a few minutes for this status to update.
              Please do not reload the page.
            </small>
          </b>
          :
          <b>Status: <span className="text-muted">Address expired</span></b>
        }
      </p>
      {
        donation ?
        <p>
          We received your donation and it is now being processed. You will
          receive an email confirmation once the transaction is fully confirmed.
        </p>
        : timeLeft > 0 ?
        <p>
          This address will expire in <b>{lpad(minutes)}:{lpad(seconds)}</b>
        </p>
        :
        <p>
          We did not receive your donation in the allotted time.
          Please refresh and try again.
        </p>
      }
      <CarbonOffset />
    </div>
  )
}

export default Info
